'use client';
import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface StorageMeterProps {
  used: number;
  total: number;
  showUpgrade?: boolean;
}

export default function StorageMeter({ used, total, showUpgrade = true }: StorageMeterProps) {
  const pct = Math.min((used / total) * 100, 100);
  const isWarning = pct >= 90;
  const isCritical = pct >= 100;

  const barColor = isCritical ? 'bg-danger' : isWarning ? 'bg-warning' : 'bg-primary';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-600 text-foreground">Photos Used</span>
        <span className={`text-sm font-tabular font-700 ${isWarning ? 'text-warning' : 'text-foreground'}`}>
          {used} / {total}
        </span>
      </div>
      <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {isWarning && showUpgrade && (
        <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <AlertTriangle size={15} className="text-warning shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-600 text-orange-800">
              {isCritical ? "You've reached your photo limit." : "You're almost out of photo storage."}
            </p>
            <p className="text-xs text-orange-700 mt-0.5">
              Add another 100 photos for $19.95/year.
            </p>
          </div>
          <a href="/billing" className="text-xs font-700 text-primary hover:underline shrink-0">
            Add Storage
          </a>
        </div>
      )}
    </div>
  );
}